import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import FilterListIcon from '@material-ui/icons/FilterList';
import DropdownMenuFilter from '../../../components/DropdownMenu/DropdownMenuFilter.component';
import { useStyles } from './ExaminerTreemapTable.styles';
import { useStyles as useTableStyles } from '../../styles';
import {
  ITaskGroup,
  Examiner,
  ExaminingTeamChairmen,
  ExaminingTeamMember,
  AssementSessionData,
  IExaminingTeamMemberWithFunctionId,
  ExamTaskGroupCoordinator,
  SampleAssessmentGroup,
  SampleAssessmentGroupMember,
} from '../../../models/taskGroup.types';
import { AddSampleAssessmentGroupDialog } from '../Actions/AddSampleAssessmentGroupDialog.component';
import { PromptDetails } from '../../../models/promptsTypeModels.types';
import { Prompt, SubText } from '../../../components/Prompt/Prompt.component';
import AddKGZPrompt from '../Actions/AddKGZPrompt.component';
import UnassignExamTaskGroupCoordinatorDialog from '../Actions/UnassignExamTaskGroupCoordinatorDialog.component';
import { getExaminerNumberFromPermission, getIsExaminerBlocked, isExpanded } from '../Helpers';
import DisableExaminer from '../Actions/DisableExaminer.component';
import EnableExaminer from '../Actions/EnableExaminer.component';
import { SampleAssessmentGroupChairmanAssignDialog } from '../Actions/SampleAssessmentGroupChairmanAssignDialog.component';
import FilterStateGroup from '../../../models/filterStateGroup.types';
import { SessionsParametersCardLimitPerExaminerDialog } from '../../Sessions/SessionsParametersDialog/SessionsParametersCardLimitPerExaminer/SessionsParametersCardLimitPerExaminerDialog';
import { ExaminerType } from '../ExaminerTreemap.types';
import {
  addExaminingTeamChairman,
  deleteExaminingTeamChairman,
  deleteSampleAssessmentGroup,
  unassignExaminingTeamChairman,
} from '../../../services/exams/examiningTeam/examiningTeam.service';
import {
  unassignSampleGroupAssesmentChairman,
  deleteAssessmentTeamMember,
} from '../../../services/exams/sampleAssessmentGroup/sampleAssessmentGroup.service';
import { NoRecords } from '../../../components/NoRecords/NoRecords.component';
import TooltipOne from '../../../components/TooltipOne/TooltipOne.component';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import { ExpandableArrowIcon } from './ExpandableArrowIcon.component';
import useSessionUser from '../../../hooks/useSessionUser';
import { ExaminerFunction } from '../../Assessment/Assessment.types';
import ManualLockedPersonIcon from '@mui/icons-material/LockPersonOutlined';
import AutoLockedPersonIcon from '../../../assets/icons/auto-locked-red.svg';
import { DisableExaminingTeam } from '../Actions/DisableExaminingTeam.component';
import { BlockadeTypeId } from '../../../components/ExaminersStructure/ExaminersStructure.types';
import { NewMessageModal } from '../../../components/NewMessage/NewMessageModal.component';
import { useOpenAlert } from '../../../Context/Layout.context';
import { SelectableTable } from '../../../components/SelectableTable/SelectableTable.component';
import { SelectableTableColumn } from '../../../components/SelectableTable/SelectableTable.types';
import { useTranslation } from 'react-i18next';
import ActionsMenu from '../../../components/ActionsMenu/ActionsMenu.component';
import { ActionMenuItem } from '../../../components/ActionsMenu/ActionsMenu.types';
import { fill, fillRed } from '../../../utils/contrastSvgAttributes';
import { determineTableSubheaderRowBackgroundColor } from './helpers';
import { HandleClickMenuFunction } from './ExaminerTreemapTable.types';
import { areObjectsEqual } from '../../../utils/areObjectsEqual';
import { CircularProgress } from '@material-ui/core';

export interface ExaminerTreemapTableProps {
  data: ITaskGroup[];
  expandedItems: string[];
  handleExpandedItems: (id?: string) => void;
  filterTaskGroupsMenuItems: string[];
  filterFunctionsMenuItems: string[];
  firstNameSearchInput: string;
  lastNameSearchInput: string;
  examinerNumberSearchInput: string;
  handleSearchInput: (name: string, value: string) => void;
  reload: () => void;
  handleClickTaskGroupMenu: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  handleClickFunctionMenu: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  checkedTaskGroupsMenuItems: string[];
  checkedFunctionsMenuItems: string[];
  setCheckedTaskGroupsMenuItems: (items: string[]) => void;
  setCheckedFunctionsMenuItems: (items: string[]) => void;
  handleTaskGroupFilterChange: (item: string) => void;
  handleFunctionsFilterChange: (item: string) => void;
  uncheckAllTaskGroups: () => void;
  uncheckAllFunctions: () => void;
  uncheckAllFilters: () => void;
  onModalFunctionAction: (data: PromptDetails) => void;
  isAnyFiltersActive: boolean;
  filterState: FilterStateGroup[];
  handleCloseFilterItem: (item: string) => void;
  openAssignModal: (title: string, type: ExaminerType, isMultiChoice?: boolean, assessmentId?: string) => void;
  isAnyInputActive: boolean;
  isLoader: boolean;
  isTableScrolled?: boolean;
  examinerMode?: boolean;
  isControlProcessActive: boolean;
  assessmentSessionId: string;
  assessmentSessionName: string;
}

export interface ShareExaminterTreeMapFunction {
  ReloadTreeMap: () => void;
  CloseMenu: () => void;
  selectedItemId: string;
  selectedSecondaryId: string | undefined;
}

interface ExaminerTreemapTableItem {
  [key: string]: string | number | boolean | JSX.Element | ReactNode;
  id: string;
  taskGroupName?: ReactNode;
  firstName?: ReactNode;
  lastName?: ReactNode;
  examinerNumber?: ReactNode;
  actions: JSX.Element;
  rowBackground?: string;
}

let undefinedParameter: ITaskGroup | undefined;

export interface FlatExaminerTreemapTableItem {
  [index: string]: {
    row: ExaminerTreemapTableItem;
    rowData: unknown;
    rowGetterFunction: (rowData: unknown) => ExaminerTreemapTableItem;
  };
}

const treemapItemsPagination = 100;
export const ExaminerTreemapTable = forwardRef<ShareExaminterTreeMapFunction | undefined, ExaminerTreemapTableProps>(
  (props, ref) => {
    const {
      examinerMode,
      expandedItems,
      handleCloseFilterItem,
      handleExpandedItems,
      onModalFunctionAction,
      openAssignModal,
      reload,
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();
    const tableClasses = useTableStyles();
    const [examiners, setExaminers] = useState<ITaskGroup[] | null>(null);
    const [openDisableExaminerPrompt, setOpenDisableExaminerPrompt] = useState<boolean>(false);
    const [openDisableExaminingTeamPrompt, setOpenDisableExaminingTeamPrompt] = useState<boolean>(false);
    const [openEnableExaminerPrompt, setOpenEnableExaminerPrompt] = useState<boolean>(false);
    const selectedItemId = useRef<string>('');
    const selectedAssessmentSessionId = useRef<string>('');
    const selectedTaskGroup = useRef<ITaskGroup>();
    const selectedExaminer = useRef<Examiner | undefined>();
    const examiningTeamMember = useRef<IExaminingTeamMemberWithFunctionId>();
    const selectedSecondaryId = useRef<string>('');
    const [selectedSampleAsssessmentGroup, setSelectedSampleAsssessmentGroup] = useState<string>();
    const [menuAnchor, setMenuAnchor] = useState<SVGSVGElement>();
    const [anchorElTaskGroup, setAnchorElTaskGroup] = useState<SVGSVGElement | HTMLElement | null>(null);
    const [action, setAction] = useState<string>('');
    const [anchorElFunction, setAnchorElFunction] = useState<SVGSVGElement | HTMLElement | null>(null);
    const [isAddSampleAssessmentGroupConfirmationVisible, setIsAddSampleAssessmentGroupConfirmationVisible] =
      useState<boolean>(false);
    const [isAssignSampleAssessmentGroupChairmantDialogVisible, setIsAssignSampleAssessmentGroupChairmanDialogVisible] =
      useState<boolean>(false);
    const [openLimitCardPerExaminerDialog, setOpenLimitCardPerExaminerDialog] = useState<boolean>(false);
    const [openUnassignDialog, setOpenUnassignDialog] = useState<boolean>(false);
    const [openAddKGZPrompt, setOpenKGZPrompt] = useState<boolean>(false);
    const [isConfirmationPromptVisible, setIsConfirmationPromptVisible] = useState<boolean>(false);
    const [isSuccessPromptVisible, setIsSuccessPromptVisible] = useState<boolean>(false);
    const [isErrorPromptVisible, setIsErrorPromptVisible] = useState<boolean>(false);
    const [promptMainText, setPromptMainText] = useState<string>('');
    const [promptSubTexts, setPromptSubTexts] = useState<SubText[]>();
    const RemovedFunctionKgzForGroupId = useRef<string>('');
    const RemovedFunctionKgzForGroupGroupName = useRef<string>('');
    const RemovedExaminatorFromZeFullName = useRef<string>('');
    const RemovedExaminatorFromZeByTeamMemberId = useRef<string>('');
    const [IsEmptyResult, setIsEmptyResult] = useState<boolean>(false);
    const [examinersFound, setExaminersFound] = useState<string[]>([]);
    const [isExaminingTeamChairmen, setIsExaminingTeamChairmen] = useState<boolean>(false);
    const { userAssessmentSessionInfo, isSessionLoaded } = useSessionUser();
    const examinerFunction = userAssessmentSessionInfo?.function;
    const [messageModal, setMessageModal] = useState<{ open: boolean; type: 'standard' | 'helpdesk' }>({
      open: false,
      type: 'standard',
    });

    const [renderStructureItemsCount, setRenderStructureItemsCount] = useState(treemapItemsPagination);
    const allTreemapItemsCount = useRef(0);

    const defaultFilter = useMemo(
      () => ({
        taskGroupName: '',
        firstName: props.firstNameSearchInput,
        lastName: props.lastNameSearchInput,
        examinerNumber: props.examinerNumberSearchInput,
      }),
      [props.examinerNumberSearchInput, props.firstNameSearchInput, props.lastNameSearchInput],
    );

    const [treemapData, setTreemapData] = useState<ExaminerTreemapTableItem[] | undefined>(undefined);
    const allTreemapDataItems = useRef<FlatExaminerTreemapTableItem>({});

    const getNewRow = function <T>(id: string, rowData: T, getRowGetter: (rowData: T) => ExaminerTreemapTableItem) {
      const prevRowItem = allTreemapDataItems.current[id];

      if (
        areObjectsEqual(prevRowItem?.rowData, rowData as Partial<T>) &&
        prevRowItem?.rowGetterFunction === getRowGetter
      ) {
        return prevRowItem.row;
      }

      const newRow = getRowGetter(rowData);
      allTreemapDataItems.current[id] = {
        rowGetterFunction: getRowGetter as (rowData: unknown) => ExaminerTreemapTableItem,
        row: newRow,
        rowData,
      };
      return newRow;
    };

    const openAlert = useOpenAlert();

    const hasAdminExaminersLineupsManagePermisson = usePermission(UserPermission.AdminExaminersLineupsManage);
    const hasAdminOkeExaminersLineupsManagePermission = usePermission(UserPermission.AdminOkeExaminersLineupsManage);

    const handleClickMenu: HandleClickMenuFunction = useCallback(
      (
        itemId: string,
        taskGroup?: ITaskGroup,
        examiner?: Examiner,
        secondaryId?: string,
        sampleAssessmentGroupId?: string,
        teamMember?: ExaminingTeamMember,
        item?: any,
        assessmentId?: string,
      ) => {
        selectedItemId.current = itemId;
        if (assessmentId) {
          selectedAssessmentSessionId.current = assessmentId;
        }
        if (taskGroup) {
          selectedTaskGroup.current = taskGroup;
          RemovedFunctionKgzForGroupId.current = itemId;
          RemovedFunctionKgzForGroupGroupName.current = taskGroup.name;
        }
        if (examiner) {
          selectedExaminer.current = examiner;
          RemovedExaminatorFromZeFullName.current = examiner.firstName + ' ' + examiner.lastName;
          RemovedExaminatorFromZeByTeamMemberId.current = teamMember ? teamMember.teamMemberId : '';
        }
        if (secondaryId) {
          selectedSecondaryId.current = secondaryId;
        }
        if (sampleAssessmentGroupId) {
          setSelectedSampleAsssessmentGroup(sampleAssessmentGroupId);
        }
        if (item) {
          examiningTeamMember.current = item;
        }
      },
      [],
    );

    const hasPermissionToManageActions = examinerMode
      ? true
      : hasAdminExaminersLineupsManagePermisson || hasAdminOkeExaminersLineupsManagePermission;

    const hasPermissionForGroupTaskActions = examinerMode
      ? examinerFunction === ExaminerFunction.KK
      : hasAdminExaminersLineupsManagePermisson || hasAdminOkeExaminersLineupsManagePermission;

    const hasPermissionForExamTaskGroupCoordinatorActions = examinerMode
      ? [ExaminerFunction.KK, ExaminerFunction.KGZ].includes(examinerFunction)
      : hasAdminExaminersLineupsManagePermisson || hasAdminOkeExaminersLineupsManagePermission;

    const displayRemoveSampleAssessmentGroupPrompt = useCallback(() => {
      const taskGroup = props.data.find(
        (taskGroup) => taskGroup.sampleAssessmentGroup?.sampleAssessmentsGroupId === selectedItemId.current,
      );
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        setPromptMainText('Czy na pewno chcesz usunąć Zespół Wzorcowych Ocen (ZWO) z:');
        const subTexts: SubText[] = [
          {
            text: `${taskGroup.name}`,
          },
        ];
        setPromptSubTexts(subTexts);
        setIsConfirmationPromptVisible(true);
      }
    }, [props.data]);

    const displayRemoveSampleAssessmentGroupChairmanAssignmentPrompt = useCallback(() => {
      const taskGroup = props.data.find(
        (taskGroup) =>
          taskGroup.sampleAssessmentGroup?.sampleAssessmentChairman.sampleAssessmentChairmanId ===
          selectedItemId.current,
      );
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        setPromptMainText('Czy na pewno chcesz usunąć Egzaminatorowi:');
        const subTexts: SubText[] = [
          {
            text: `${taskGroup.sampleAssessmentGroup?.sampleAssessmentChairman.examiner.firstName} ${taskGroup.sampleAssessmentGroup?.sampleAssessmentChairman.examiner.lastName}`,
          },
          { text: 'przypisaną funkcję Przewodniczącego ZWO?', isBold: true },
        ];
        setPromptSubTexts(subTexts);
        setIsConfirmationPromptVisible(true);
      }
    }, [props.data]);

    const displayRemoveSampleAssessmentGroupMemberAssignmentPrompt = useCallback(() => {
      const taskGroup = props.data.find((taskGroup) =>
        taskGroup.sampleAssessmentGroup?.sampleAssessmentMembers.find(
          (member) => member.sampleAssessmentMemberId === selectedItemId.current,
        ),
      );
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        setPromptMainText('Czy na pewno chcesz usunąć członka Zespołu Wzorcowych Ocen (ZWO)');
        const subTexts: SubText[] = [
          {
            text: `${
              taskGroup.sampleAssessmentGroup?.sampleAssessmentMembers.find(
                (member) => member.sampleAssessmentMemberId === selectedItemId.current,
              )?.examiner.firstName
            } ${
              taskGroup.sampleAssessmentGroup?.sampleAssessmentMembers.find(
                (member) => member.sampleAssessmentMemberId === selectedItemId.current,
              )?.examiner.lastName
            }`,
          },
        ];
        setPromptSubTexts(subTexts);
        setIsConfirmationPromptVisible(true);
      }
    }, [props.data]);

    const getTaskGroupActionsMenu = useCallback(
      (item: ITaskGroup, rowIndex: number): JSX.Element => {
        const menuItems: ActionMenuItem[] = [];
        menuItems.push({
          text: 'Dodaj funkcję KGZ',
          onClick: () => {
            setAction('Dodaj funkcję KGZ');
            setOpenKGZPrompt(true);
          },
        });
        if (!item.sampleAssessmentGroup) {
          menuItems.push({
            text: 'Dodaj ZWO',
            onClick: () => {
              setAction('Dodaj ZWO');
              setIsAddSampleAssessmentGroupConfirmationVisible(true);
            },
          });
        }
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [setAction],
    );
    const getTaskGroupRow = useCallback(
      (taskGroupRowData: {
        assessmentId: string;
        id: string;
        isExpanded: boolean;
        item: ITaskGroup;
        rowIndex: number;
        tooltipTitle: string;
      }) => {
        const { assessmentId, id, isExpanded, item, rowIndex, tooltipTitle } = taskGroupRowData;

        const newRow = {
          id,
          taskGroupName: (
            <TooltipOne title={tooltipTitle} placement="top">
              <>
                <div className={classes.taskGroupHeader}>
                  <ExpandableArrowIcon
                    className={classes.dropdownIcon}
                    isExpanded={isExpanded}
                    ariaControls={'task-group-header'}
                    onClick={() => handleExpandedItems(item.taskGroupId)}
                  />
                  {item.name}
                </div>
              </>
            </TooltipOne>
          ),
          actions: (
            <>
              {hasPermissionForGroupTaskActions && (
                <div
                  onClick={() =>
                    handleClickMenu(
                      item.taskGroupId,
                      item,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      assessmentId,
                    )
                  }
                >
                  {getTaskGroupActionsMenu(item, rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground: 'rgba(202, 202, 252, 0.2)',
        };
        return newRow;
      },
      [
        classes.dropdownIcon,
        classes.taskGroupHeader,
        getTaskGroupActionsMenu,
        handleClickMenu,
        handleExpandedItems,
        hasPermissionForGroupTaskActions,
      ],
    );

    const treemapColumns: SelectableTableColumn[] = useMemo(
      () => [
        {
          id: 'taskGroupName',
          type: 'text',
          title: t('examinersTreemap:table.taskGroupName'),
          filterType: 'filter',
        },
        {
          id: 'function',
          type: 'text',
          title: t('examinersTreemap:table.function'),
          filterType: 'filter',
        },
        {
          id: 'firstName',
          type: 'text',
          title: t('examinersTreemap:table.firstName'),
          filterType: 'search',
        },
        {
          id: 'lastName',
          type: 'text',
          title: t('examinersTreemap:table.lastName'),
          filterType: 'search',
        },
        {
          id: 'examinerNumber',
          type: 'text',
          title: t('examinersTreemap:table.examinerNumber'),
          filterType: 'search',
        },
        {
          id: 'actions',
          type: 'actions',
          title: 'Akcje',
          filterType: null,
          skipEnterMethod: true,
        },
      ],
      [t],
    );

    useEffect(() => {
      if (props.data) {
        setExaminers(props.data);
      }
    }, [props.data]);

    const closeMenu = () => {
      setMenuAnchor(undefined);
    };

    useEffect(() => {
      setMenuAnchor(undefined);
    }, [props.isTableScrolled]);

    const handleReload = useCallback(() => {
      selectedItemId.current = '';
      closeMenu();
      reload();
    }, [reload]);

    const handleClickTaskGroupMenu = useCallback(
      (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        if (!anchorElTaskGroup) {
          setAnchorElTaskGroup(event.currentTarget);
        } else setAnchorElTaskGroup(null);
      },
      [anchorElTaskGroup],
    );

    const handleClickFunctionMenu = useCallback(
      (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        if (!anchorElTaskGroup) {
          setAnchorElFunction(event.currentTarget);
        } else setAnchorElFunction(null);
      },
      [anchorElTaskGroup],
    );

    useImperativeHandle(ref, () => ({
      ReloadTreeMap() {
        closeMenu();
        reload();
      },
      CloseMenu() {
        closeMenu();
      },
      selectedItemId: selectedItemId.current,
      selectedSecondaryId: selectedSecondaryId.current,
    }));

    const addExaminingTeamChairmanFunction = useCallback(() => {
      const taskGroup = props.data.find(
        (taskGroup) =>
          taskGroup.examTaskGroupCoordinators.findIndex(
            (examTaskGroupCoordinator) => examTaskGroupCoordinator.taskGroupCoordinatorId === selectedItemId.current,
          ) != -1,
      );
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        addExaminingTeamChairman(selectedItemId.current).then(
          () => {
            handleReload();
          },
          () => {
            setPromptMainText('Dodanie funkcji PZE dla:');
            setPromptSubTexts([
              { text: taskGroup.name },
              {
                text: 'nie powiodło się, spróbuj ponownie później.',
                isBold: true,
              },
            ]);
            setIsErrorPromptVisible(true);
          },
        );
      }
    }, [handleReload, props.data]);

    const displayRemoveExaminingTeamChairmanFunctionPrompt = useCallback(() => {
      const taskGroup = props.data.find(
        (taskGroup) =>
          taskGroup.examTaskGroupCoordinators.findIndex(
            (examTaskGroupCoordinator) =>
              examTaskGroupCoordinator.examiningTeamChairmen.findIndex(
                (examiningTeamChairmen) => examiningTeamChairmen.teamChairmanId === selectedItemId.current,
              ) !== -1,
          ) !== -1,
      );
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        setPromptMainText('Czy na pewno chcesz usunąć funkcję PZE z:');
        setPromptSubTexts([{ text: taskGroup.name }]);
        setIsConfirmationPromptVisible(true);
      }
    }, [props.data]);

    const displayRemoveExaminingTeamChairmanAssingmentFunctionPrompt = useCallback(() => {
      const taskGroup = props.data.find(
        (taskGroup) =>
          taskGroup.examTaskGroupCoordinators.findIndex(
            (examTaskGroupCoordinator) =>
              examTaskGroupCoordinator.examiningTeamChairmen.findIndex(
                (examiningTeamChairmen) => examiningTeamChairmen.teamChairmanId === selectedItemId.current,
              ) !== -1,
          ) !== -1,
      );
      if (taskGroup) {
        selectedTaskGroup.current = taskGroup;
        setPromptMainText('Czy na pewno chcesz usunąć Egzaminatorowi:');
        const chariman = taskGroup.examTaskGroupCoordinators
          .find((coordinator) =>
            coordinator.examiningTeamChairmen.find((chairman) => chairman.teamChairmanId === selectedItemId.current),
          )
          ?.examiningTeamChairmen.find((chairman) => chairman.teamChairmanId == selectedItemId.current)?.examiner;

        const subTexts: SubText[] = [
          { text: `${chariman?.firstName} ${chariman?.lastName}` },
          { text: 'przypisaną funkcję PZE?', isBold: true },
        ];
        setPromptSubTexts(subTexts);
        setIsConfirmationPromptVisible(true);
      }
    }, [props.data]);

    const removeExaminignTeamChairmanAssignment = () => {
      setPromptMainText('Usunięcie przypisanej funkcji PZE Egzaminatorowi:');
      const subTexts: SubText[] = [];
      if (selectedTaskGroup.current) {
        subTexts.push({
          text: `${
            selectedTaskGroup.current.examTaskGroupCoordinators
              .find((coordinator) =>
                coordinator.examiningTeamChairmen.find(
                  (chairman) => chairman.teamChairmanId === selectedItemId.current,
                ),
              )
              ?.examiningTeamChairmen.find((chairman) => chairman.teamChairmanId == selectedItemId.current)?.examiner
              .firstName
          } ${
            selectedTaskGroup.current.examTaskGroupCoordinators
              .find((coordinator) =>
                coordinator.examiningTeamChairmen.find(
                  (chairman) => chairman.teamChairmanId === selectedItemId.current,
                ),
              )
              ?.examiningTeamChairmen.find((chairman) => chairman.teamChairmanId == selectedItemId.current)?.examiner
              .lastName
          }`,
        });
      }
      unassignExaminingTeamChairman(selectedItemId.current).then(
        () => {
          subTexts.push({ text: 'zakończone sukcesem.', isBold: true });
          setPromptSubTexts(subTexts);
          setIsSuccessPromptVisible(true);
          closeMenu();
          reload();
        },
        () => {
          subTexts.push({
            text: 'nie powiodło się, spróbuj ponownie później.',
            isBold: true,
          });
          setPromptSubTexts(subTexts);
          setIsErrorPromptVisible(true);
        },
      );
    };

    const removeExaminingTeamChairmanFunction = () => {
      setPromptMainText('Usunięcie funkcji PZE z:');
      const subTexts: SubText[] = [];
      if (selectedTaskGroup.current) {
        subTexts.push({ text: selectedTaskGroup.current.name });
      }

      deleteExaminingTeamChairman(selectedItemId.current).then(
        () => {
          subTexts.push({ text: 'zakończone sukcesem.', isBold: true });
          setPromptSubTexts(subTexts);
          setIsSuccessPromptVisible(true);
          closeMenu();
          reload();
        },
        () => {
          subTexts.push({
            text: 'nie powiodło się, spróbuj ponownie później.',
            isBold: true,
          });
          setPromptSubTexts(subTexts);
          setIsErrorPromptVisible(true);
        },
      );
    };

    const removeSampleAssessmentChairmanAssignment = () => {
      setPromptMainText('Usunięcie przypisanej funkcji Przewodniczącego ZWO Egzaminatorowi:');
      const subTexts: SubText[] = [];
      if (selectedTaskGroup.current) {
        subTexts.push({
          text: `${selectedTaskGroup.current.sampleAssessmentGroup?.sampleAssessmentChairman.examiner.firstName} ${selectedTaskGroup.current.sampleAssessmentGroup?.sampleAssessmentChairman.examiner.lastName}`,
        });
      }
      unassignSampleGroupAssesmentChairman(selectedItemId.current).then(
        () => {
          subTexts.push({ text: 'zakończone sukcesem.', isBold: true });
          setPromptSubTexts(subTexts);
          setIsSuccessPromptVisible(true);
          reload();
        },
        () => {
          subTexts.push({
            text: 'nie powiodło się, spróbuj ponownie później.',
            isBold: true,
          });
          setPromptSubTexts(subTexts);
          setIsErrorPromptVisible(true);
        },
      );
    };

    const removeSampleAssessmentGroup = () => {
      setPromptMainText('Usunięcie Zespołu Wzorcowych Ocen (ZWO) z:');
      const subTexts: SubText[] = [];
      if (selectedTaskGroup.current) {
        subTexts.push({
          text: `${selectedTaskGroup.current.name}`,
        });
      }
      deleteSampleAssessmentGroup(selectedItemId.current).then(
        () => {
          subTexts.push({ text: 'zakończone sukcesem.', isBold: true });
          setPromptSubTexts(subTexts);
          setIsSuccessPromptVisible(true);
          reload();
        },
        () => {
          subTexts.push({
            text: 'nie powiodło się, spróbuj ponownie później.',
            isBold: true,
          });
          setPromptSubTexts(subTexts);
          setIsErrorPromptVisible(true);
        },
      );
    };

    const removeSampleAssessmentMemberAssignment = () => {
      deleteAssessmentTeamMember(selectedItemId.current).then(
        () => {
          openAlert('success');
          reload();
        },
        () => {
          setPromptMainText('Nie można usunąć członka ZWO:');
          const subTexts: SubText[] = [];
          if (selectedTaskGroup.current) {
            subTexts.push({
              text: `${
                selectedTaskGroup.current.sampleAssessmentGroup?.sampleAssessmentMembers.find(
                  (member) => member.sampleAssessmentMemberId === selectedItemId.current,
                )?.examiner.firstName
              } ${
                selectedTaskGroup.current.sampleAssessmentGroup?.sampleAssessmentMembers.find(
                  (member) => member.sampleAssessmentMemberId === selectedItemId.current,
                )?.examiner.lastName
              }`,
            });
          }
          subTexts.push({ text: 'ponieważ przynajmniej jedno RGZ zostało', isBold: true });
          subTexts.push({ text: 'ocenione i odesłane do weryfikacji.', isBold: true });
          setPromptSubTexts(subTexts);
          setIsErrorPromptVisible(true);
        },
      );
    };

    const determineRowBackgroundColor = useCallback(
      (id: string, shouldBeHighlighted: boolean, assessmentId: string): string => {
        if (id !== selectedItemId.current && assessmentId !== selectedAssessmentSessionId.current) {
          if (shouldBeHighlighted) {
            return 'rgba(202, 202, 252, 0.7)';
          } else return '';
        } else {
          return id === selectedItemId.current && assessmentId === selectedAssessmentSessionId.current
            ? 'rgba(148, 148, 203, 0.31)'
            : '';
        }
      },
      [],
    );

    useEffect(() => {
      if (!props.data) return;
      if (examiners?.length === 0) {
        setIsEmptyResult(true);
      } else setIsEmptyResult(false);
    }, [examiners, props.data]);

    useEffect(() => {
      if (!props.data) return;
      if (examiners?.length && props.isAnyInputActive && props.checkedFunctionsMenuItems.length > 0) {
        const foundIds: string[] = [];
        setExaminersFound([]);
        examiners.forEach((taskGroup: ITaskGroup) => {
          if (taskGroup.sampleAssessmentGroup) {
            const ZWO = taskGroup.sampleAssessmentGroup;
            if (ZWO) {
              if (ZWO.sampleAssessmentChairman.examiner) {
                if (
                  ZWO.sampleAssessmentChairman.examiner?.shouldBeHighlighted &&
                  props.checkedFunctionsMenuItems.includes('ZWO')
                ) {
                  foundIds.push(ZWO.sampleAssessmentChairman.examiner.examinerId);
                  setExaminersFound(foundIds);
                }
              }
            }
          }
          const KGZ = taskGroup.examTaskGroupCoordinators;
          if (KGZ) {
            KGZ.forEach((kgz) => {
              if (kgz.examiner?.shouldBeHighlighted && props.checkedFunctionsMenuItems.includes('KGZ')) {
                foundIds.push(kgz.examiner.examinerId);
                setExaminersFound(foundIds);
              }
              kgz.examiningTeamChairmen.forEach((examiningTeamChairmen: ExaminingTeamChairmen) => {
                if (
                  examiningTeamChairmen.examiner?.shouldBeHighlighted &&
                  props.checkedFunctionsMenuItems.includes('PZE')
                ) {
                  foundIds.push(examiningTeamChairmen.examiner.examinerId);
                  setExaminersFound(foundIds);
                }
                examiningTeamChairmen.examiningTeam.examiningTeamMembers.forEach((member) => {
                  if (member) {
                    if (
                      member.examiner?.shouldBeHighlighted &&
                      props.checkedFunctionsMenuItems.includes('Egzaminator')
                    ) {
                      foundIds.push(member.examiner.examinerId);
                      setExaminersFound(foundIds);
                    }
                  }
                });
              });
            });
          }
        });
      }
    }, [examiners, props.checkedFunctionsMenuItems, props.data]);

    useEffect(() => {
      if (examinersFound.length === 0 && props.isAnyInputActive && props.checkedFunctionsMenuItems.length > 0) {
        setExaminers([]);
      } else setExaminers(props.data);
    }, [examinersFound, props.checkedFunctionsMenuItems]);

    useEffect(() => {
      setExaminers(props.data);
    }, [props.checkedFunctionsMenuItems]);

    const closeFilterItem = useCallback(
      (item?: string) => {
        if (item) handleCloseFilterItem(item);
      },
      [handleCloseFilterItem],
    );

    const sessionDataForNewMessage: AssementSessionData = {
      id: props.assessmentSessionId,
      name: props.assessmentSessionName,
      sampleAssessmentGroupName: selectedTaskGroup.current ? selectedTaskGroup.current.name : '',
      assessmentSessionId: selectedAssessmentSessionId.current ? selectedAssessmentSessionId.current : '',
      examTaskGroupName: selectedTaskGroup.current ? selectedTaskGroup.current.name : '',
    };

    const getSampleAssessmentGroupMemberActionsMenu = useCallback(
      (rowIndex: number): JSX.Element => {
        const menuItems: ActionMenuItem[] = [];

        menuItems.push({
          text: 'Usuń członka ZWO',
          onClick: () => {
            setAction('Usuń członka ZWO');
            displayRemoveSampleAssessmentGroupMemberAssignmentPrompt();
          },
        });
        if (hasPermissionForGroupTaskActions) {
          menuItems.push({
            text: 'Wyślij wiadomość',
            onClick: () => {
              setAction('Wyślij wiadomość');
              setMessageModal({ open: true, type: 'standard' });
            },
          });
        }
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [
        displayRemoveSampleAssessmentGroupMemberAssignmentPrompt,
        hasPermissionForGroupTaskActions,
        setAction,
        setMessageModal,
      ],
    );
    const getSampleAssessmentMemberRow = useCallback(
      (sampleAssessmentMemberRowData: {
        member: SampleAssessmentGroupMember;
        assessmentId: string;
        taskGroup: ITaskGroup;
        rowIndex: number;
        rowBackground: string;
      }) => {
        const { member, assessmentId, taskGroup, rowIndex, rowBackground } = sampleAssessmentMemberRowData;

        return {
          id: member.sampleAssessmentMemberId,
          function: (
            <div
              style={{
                transform: 'translateX(40px)',
              }}
            >
              Członek ZWO
            </div>
          ),
          firstName: member.examiner.firstName,
          lastName: member.examiner.lastName,
          examinerNumber: getExaminerNumberFromPermission(member.examiner, assessmentId),
          actions: (
            <>
              {hasPermissionForGroupTaskActions && (
                <div
                  onClick={() => {
                    if (taskGroup.sampleAssessmentGroup) {
                      handleClickMenu(
                        member.sampleAssessmentMemberId,
                        taskGroup,
                        member.examiner,
                        undefined,
                        undefined,
                        undefined,
                        member,
                        assessmentId,
                      );
                    }
                  }}
                >
                  {getSampleAssessmentGroupMemberActionsMenu(rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground,
        };
      },
      [getSampleAssessmentGroupMemberActionsMenu, handleClickMenu, hasPermissionForGroupTaskActions],
    );

    const getExamTaskGroupCoordinatorActionsMenu = useCallback(
      (examTaskGroupCoordinator: ExamTaskGroupCoordinator, assessmentId: string, rowIndex: number): JSX.Element => {
        const menuItems: ActionMenuItem[] = [];

        if (examTaskGroupCoordinator?.examiner?.examinerId) {
          menuItems.push({
            text: 'Usuń egzaminatorowi funkcję KGZ',
            onClick: () => {
              setAction('Usuń egzaminatorowi funkcję KGZ');
              setOpenUnassignDialog(true);
            },
          });
        }
        if (!examTaskGroupCoordinator?.examiner?.examinerId) {
          menuItems.push({
            text: 'Przypisz egzaminatorowi funkcję KGZ',
            onClick: () => {
              setAction('Przypisz egzaminatorowi funkcję KGZ');
              openAssignModal(
                `Przypisz funkcję Koordynatora Grupy Zadań (${RemovedFunctionKgzForGroupGroupName.current})`,
                'KGZ',
                false,
                assessmentId,
              );
            },
          });
        }
        menuItems.push({
          text: 'Dodaj funkcję PZE',
          onClick: () => {
            setAction('Dodaj funkcję PZE');
            addExaminingTeamChairmanFunction();
          },
        });
        if (hasPermissionForGroupTaskActions && examTaskGroupCoordinator?.examiner) {
          menuItems.push({
            text: 'Wyślij wiadomość',
            onClick: () => {
              setAction('Wyślij wiadomość');
              setMessageModal({ open: true, type: 'standard' });
            },
          });
        }
        if (!examTaskGroupCoordinator?.examiner?.examinerId) {
          menuItems.push({
            text: 'Usuń funkcję KGZ',
            onClick: () => {
              setAction('Usuń funkcję KGZ');
              onModalFunctionAction({
                fullName: '',
                examinatorId: '',
                type: 'confirmation',
                otherText: RemovedFunctionKgzForGroupGroupName.current,
                groupId: RemovedFunctionKgzForGroupId.current,
                typePromp: 'KGZ',
              });
              closeMenu();
            },
          });
        }
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [addExaminingTeamChairmanFunction, hasPermissionForGroupTaskActions, onModalFunctionAction, openAssignModal],
    );
    const getExamTaskGroupCoordinatorRow = useCallback(
      (examTaskGroupCoordinatorRowData: {
        id: string;
        firstName: string;
        lastName: string;
        examinerNumber: string;
        assessmentId: string;
        tooltipTitle: string;
        examTaskGroupCoordinator: ExamTaskGroupCoordinator;
        isExpanded: boolean;
        rowBackground: string;
        rowIndex: number;
        taskGroup: ITaskGroup;
      }) => {
        const {
          id,
          firstName,
          lastName,
          examinerNumber,
          tooltipTitle,
          isExpanded,
          examTaskGroupCoordinator,
          taskGroup,
          assessmentId,
          rowBackground,
          rowIndex,
        } = examTaskGroupCoordinatorRowData;

        return {
          id,
          function: (
            <TooltipOne title={tooltipTitle} placement="top">
              <div className={classes.subHeader}>
                <ExpandableArrowIcon
                  className={classes.dropdownIcon}
                  isExpanded={isExpanded}
                  ariaControls={'KGZ-header'}
                  onClick={() => handleExpandedItems(examTaskGroupCoordinator.taskGroupCoordinatorId)}
                />
                Koordynator grupy zadań
              </div>
            </TooltipOne>
          ),
          firstName,
          lastName,
          examinerNumber,
          actions: (
            <>
              {hasPermissionForExamTaskGroupCoordinatorActions && (
                <div
                  onClick={() => {
                    handleClickMenu(
                      examTaskGroupCoordinator.taskGroupCoordinatorId,
                      taskGroup,
                      examTaskGroupCoordinator.examiner,
                      undefined,
                      undefined,
                      undefined,
                      examTaskGroupCoordinator,
                      assessmentId,
                    );
                  }}
                >
                  {getExamTaskGroupCoordinatorActionsMenu(examTaskGroupCoordinator, assessmentId, rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground,
        };
      },
      [
        classes.dropdownIcon,
        classes.subHeader,
        getExamTaskGroupCoordinatorActionsMenu,
        handleClickMenu,
        handleExpandedItems,
        hasPermissionForExamTaskGroupCoordinatorActions,
      ],
    );

    const handlePZEAction = useCallback(
      (item: string, assessmentId?: string) => {
        setAction(item);

        switch (item) {
          case 'Przypisz egzaminatorowi funkcję PZE':
            openAssignModal('Przypisz egzaminatorowi funkcję PZE', 'PZE', false, assessmentId);
            break;
          case 'Dodaj Egzaminatora/ów do ZE':
            openAssignModal('Dodaj Egzaminatora/Egzaminatorów do ZE:', 'ZE', true, assessmentId);
            break;
          case 'Ustaw limity kart Egzaminatorom':
            setOpenLimitCardPerExaminerDialog(true);
            break;
          case 'Usuń egzaminatorowi funkcję PZE':
            displayRemoveExaminingTeamChairmanAssingmentFunctionPrompt();
            break;
          case 'Usuń funkcję PZE':
            displayRemoveExaminingTeamChairmanFunctionPrompt();
            break;
          case 'Zablokuj ZE':
            setOpenDisableExaminingTeamPrompt(true);
            break;
          case 'Zablokuj PZE':
            {
              setIsExaminingTeamChairmen(true);
            }
            setOpenDisableExaminerPrompt(true);
            break;
          case 'Odblokuj PZE':
            setIsExaminingTeamChairmen(true);
            setOpenEnableExaminerPrompt(true);
            break;
        }
      },
      [
        displayRemoveExaminingTeamChairmanAssingmentFunctionPrompt,
        displayRemoveExaminingTeamChairmanFunctionPrompt,
        openAssignModal,
      ],
    );

    const getExaminingTeamChairmenActionsMenu = useCallback(
      (examiningTeamChairmen: ExaminingTeamChairmen, assessmentId: string, rowIndex: number) => {
        const isQualified = examiningTeamChairmen?.examiner?.isQualified;
        const menuItems: ActionMenuItem[] = [];

        if (examiningTeamChairmen?.examiner?.examinerId) {
          menuItems.push({
            text: 'Usuń egzaminatorowi funkcję PZE',
            onClick: () => {
              handlePZEAction('Usuń egzaminatorowi funkcję PZE');
            },
          });
          menuItems.push({
            text: 'Dodaj Egzaminatora/ów do ZE',
            onClick: () => {
              handlePZEAction('Dodaj Egzaminatora/ów do ZE', assessmentId);
            },
          });
          menuItems.push({
            text: 'Ustaw limity kart Egzaminatorom',
            onClick: () => {
              handlePZEAction('Ustaw limity kart Egzaminatorom');
            },
          });
          if (examinerMode && !isQualified && !getIsExaminerBlocked(examiningTeamChairmen.examiner, assessmentId)) {
            menuItems.push({
              text: 'Zablokuj ZE',
              onClick: () => {
                handlePZEAction('Zablokuj ZE', assessmentId);
              },
            });
            menuItems.push({
              text: 'Zablokuj PZE',
              onClick: () => {
                handlePZEAction('Zablokuj PZE');
              },
            });
          }
          if (examinerMode && !isQualified && getIsExaminerBlocked(examiningTeamChairmen.examiner, assessmentId)) {
            menuItems.push({
              text: 'Zablokuj ZE',
              onClick: () => {
                handlePZEAction('Zablokuj ZE');
              },
            });
            menuItems.push({
              text: 'Odblokuj PZE',
              onClick: () => {
                handlePZEAction('Odblokuj PZE');
              },
            });
          }
          if (examinerMode && isQualified) {
            menuItems.push({
              text: 'Zablokuj ZE',
              onClick: () => {
                handlePZEAction('Zablokuj ZE');
              },
            });
          }
          menuItems.push({
            text: 'Wyślij wiadomość',
            onClick: () => {
              setAction('Wyślij wiadomość');
              setMessageModal({ open: true, type: 'standard' });
            },
          });
        } else {
          menuItems.push({
            text: 'Przypisz egzaminatorowi funkcję PZE',
            onClick: () => {
              handlePZEAction('Przypisz egzaminatorowi funkcję PZE', assessmentId);
            },
          });
          menuItems.push({
            text: 'Dodaj Egzaminatora/ów do ZE',
            onClick: () => {
              handlePZEAction('Dodaj Egzaminatora/ów do ZE', assessmentId);
            },
          });
          menuItems.push({
            text: 'Usuń funkcję PZE',
            onClick: () => {
              handlePZEAction('Usuń funkcję PZE', assessmentId);
            },
          });
        }
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [handlePZEAction, examinerMode],
    );

    const getExaminingTeamChairmenRow = useCallback(
      (examiningTeamChairmenRowData: {
        id: string;
        isRowExpanded: boolean;
        tooltipTitle: string;
        firstName: string;
        lastName: string;
        examinerNumber: string;
        isBlocked: BlockadeTypeId | undefined;
        examiningTeamChairmen: ExaminingTeamChairmen;
        assessmentId: string;
        rowBackground: string;
        taskGroup: ITaskGroup;
        rowIndex: number;
      }) => {
        const {
          id,
          isRowExpanded,
          tooltipTitle,
          firstName,
          lastName,
          examinerNumber,
          isBlocked,
          examiningTeamChairmen,
          taskGroup,
          assessmentId,
          rowIndex,
          rowBackground,
        } = examiningTeamChairmenRowData;
        return {
          id,
          function: (
            <TooltipOne title={tooltipTitle} placement="top">
              <div
                className={classes.subHeader}
                style={{
                  transform: 'translateX(20px)',
                }}
              >
                <ExpandableArrowIcon
                  className={classes.dropdownIcon}
                  isExpanded={isRowExpanded}
                  ariaControls={'PZE-header'}
                  onClick={() => handleExpandedItems(id)}
                />
                PZE
              </div>
            </TooltipOne>
          ),
          firstName,
          lastName,
          examinerNumber: (
            <div className={classes.columnWithLockIcon}>
              {examinerNumber}
              {isBlocked === BlockadeTypeId.Manual && (
                <TooltipOne title="Zablokowany ręcznie" placement="top">
                  <ManualLockedPersonIcon
                    className={classes.lockIcon}
                    style={{
                      color: fillRed,
                    }}
                  />
                </TooltipOne>
              )}
              {isBlocked === BlockadeTypeId.Automatic && (
                <TooltipOne title="Zablokowany automatycznie" placement="top">
                  <img
                    src={AutoLockedPersonIcon}
                    className={classes.autolockIcon}
                    alt="ikona blokady automatycznej"
                    style={{
                      color: fillRed,
                    }}
                  />
                </TooltipOne>
              )}
            </div>
          ),
          actions: (
            <>
              {hasPermissionToManageActions && (
                <div
                  onClick={() => {
                    handleClickMenu(
                      examiningTeamChairmen.teamChairmanId,
                      taskGroup,
                      examiningTeamChairmen.examiner,
                      examiningTeamChairmen.examiningTeam.examiningTeamId,
                      undefined,
                      undefined,
                      examiningTeamChairmen,
                      assessmentId,
                    );
                  }}
                >
                  {getExaminingTeamChairmenActionsMenu(examiningTeamChairmen, assessmentId, rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground,
        };
      },
      [
        classes.autolockIcon,
        classes.columnWithLockIcon,
        classes.dropdownIcon,
        classes.lockIcon,
        classes.subHeader,
        getExaminingTeamChairmenActionsMenu,
        handleClickMenu,
        handleExpandedItems,
        hasPermissionToManageActions,
      ],
    );

    const getExaminingTeamMemberActionsMenu = useCallback(
      (examiner: Examiner, assessmentId: string, rowIndex: number) => {
        const menuItems: ActionMenuItem[] = [];

        if (getIsExaminerBlocked(examiner, assessmentId)) {
          menuItems.push({
            text: 'Odblokuj egzaminatora',
            onClick: () => {
              setAction('Odblokuj egzaminatora');
              setIsExaminingTeamChairmen(false);
              setOpenEnableExaminerPrompt(true);
            },
          });
        } else {
          menuItems.push({
            text: 'Zablokuj egzaminatora',
            onClick: () => {
              setAction('Zablokuj egzaminatora');
              setIsExaminingTeamChairmen(false);
              setOpenDisableExaminerPrompt(true);
            },
          });
        }
        menuItems.push({
          text: 'Usuń egzaminatora z ZE',
          onClick: () => {
            setAction('Usuń egzaminatora z ZE');
            onModalFunctionAction({
              fullName: RemovedExaminatorFromZeFullName.current,
              examinatorId: RemovedExaminatorFromZeByTeamMemberId.current,
              type: 'confirmation',
              otherText: '',
              groupId: '',
              typePromp: 'ZE',
            });
          },
        });
        menuItems.push({
          text: 'Wyślij wiadomość',
          onClick: () => {
            setAction('Wyślij wiadomość');
            setMessageModal({ open: true, type: 'standard' });
          },
        });
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [onModalFunctionAction],
    );

    const getExaminingTeamMemberRow = useCallback(
      (examiningTeamMemberRowData: {
        assessmentId: string;
        examinerNumber: string;
        firstName: string;
        id: string;
        isBlocked: BlockadeTypeId | undefined;
        lastName: string;
        rowBackground: string;
        rowIndex: number;
        taskGroup: ITaskGroup;
        teamMember: ExaminingTeamMember;
      }) => {
        const {
          assessmentId,
          examinerNumber,
          firstName,
          id,
          isBlocked,
          lastName,
          rowBackground,
          rowIndex,
          taskGroup,
          teamMember,
        } = examiningTeamMemberRowData;

        return {
          id,
          function: (
            <div
              style={{
                transform: 'translateX(40px)',
              }}
            >
              Egzaminator
            </div>
          ),
          firstName,
          lastName,
          examinerNumber: (
            <div className={classes.columnWithLockIcon}>
              {examinerNumber}
              {isBlocked === BlockadeTypeId.Manual && (
                <TooltipOne title="Zablokowany ręcznie" placement="top">
                  <ManualLockedPersonIcon
                    className={classes.lockIcon}
                    style={{
                      color: fillRed,
                    }}
                  />
                </TooltipOne>
              )}
              {isBlocked === BlockadeTypeId.Automatic && (
                <div className={classes.autolockIconDiv}>
                  <TooltipOne title="Zablokowany automatycznie" placement="top">
                    <img
                      src={AutoLockedPersonIcon}
                      className={classes.autolockIcon}
                      alt="ikona blokady automatycznej"
                      style={{
                        color: fillRed,
                      }}
                    />
                  </TooltipOne>
                </div>
              )}
            </div>
          ),
          actions: (
            <>
              {hasPermissionToManageActions && (
                <div
                  onClick={() => {
                    handleClickMenu(
                      teamMember.examiner.examinerId,
                      taskGroup ?? undefinedParameter,
                      teamMember.examiner,
                      undefined,
                      undefined,
                      teamMember,
                      teamMember,
                      assessmentId,
                    );
                  }}
                >
                  {getExaminingTeamMemberActionsMenu(teamMember.examiner, assessmentId, rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground,
        };
      },
      [
        classes.autolockIcon,
        classes.autolockIconDiv,
        classes.columnWithLockIcon,
        classes.lockIcon,
        getExaminingTeamMemberActionsMenu,
        handleClickMenu,
        hasPermissionToManageActions,
      ],
    );

    const isZWOMenuDisabled = (item: ITaskGroup) => {
      return (
        !item.examTaskGroupCoordinators.filter((x) => x.examiner !== null).length &&
        item.sampleAssessmentGroup?.sampleAssessmentChairman.examiner === null
      );
    };

    const getSampleAssessmentGroupActionsMenu = useCallback(
      (item: ITaskGroup, assessmentId: string, rowIndex: number): JSX.Element => {
        const menuItems: ActionMenuItem[] = [];
        menuItems.push({
          disabled: isZWOMenuDisabled(item),
          text: 'Dodaj członka ZWO',
          onClick: () => {
            setAction('Dodaj członka ZWO');
            openAssignModal('Dodaj Egzaminatora/Egzaminatorów do ZWO:', 'ZWO', true, assessmentId);
          },
        });
        menuItems.push({
          disabled: isZWOMenuDisabled(item),
          text: 'Usuń ZWO',
          onClick: () => {
            setAction('Usuń ZWO');
            displayRemoveSampleAssessmentGroupPrompt();
          },
        });
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [displayRemoveSampleAssessmentGroupPrompt, openAssignModal, setAction],
    );

    const getSampleAssessmentGroupRow = useCallback(
      (sampleAssessmentGroupRowData: {
        assessmentId: string;
        id: string;
        isExpanded: boolean;
        taskGroup: ITaskGroup;
        rowIndex: number;
        tooltipTitle: string;
        itemIsSelected: boolean;
      }) => {
        const { assessmentId, id, isExpanded, taskGroup, rowIndex, tooltipTitle, itemIsSelected } =
          sampleAssessmentGroupRowData;

        const newRow = {
          id,
          function: (
            <TooltipOne title={tooltipTitle} placement="top">
              <div className={classes.subHeader}>
                <ExpandableArrowIcon
                  className={classes.dropdownIcon}
                  isExpanded={isExpanded}
                  ariaControls={'SampleAssessmentGroup-header'}
                  onClick={() => handleExpandedItems(taskGroup.sampleAssessmentGroup?.sampleAssessmentsGroupId)}
                />
                Zespół Wzorcowych Ocen
              </div>
            </TooltipOne>
          ),
          actions: (
            <>
              {hasPermissionForGroupTaskActions && (
                <div
                  onClick={() => {
                    if (taskGroup.sampleAssessmentGroup) {
                      handleClickMenu(
                        taskGroup.sampleAssessmentGroup.sampleAssessmentsGroupId,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        assessmentId,
                      );
                    }
                  }}
                >
                  {getSampleAssessmentGroupActionsMenu(taskGroup, assessmentId, rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground: determineTableSubheaderRowBackgroundColor(itemIsSelected /*id === selectedItemId*/),
        };
        return newRow;
      },
      [
        classes.dropdownIcon,
        classes.subHeader,
        getSampleAssessmentGroupActionsMenu,
        handleClickMenu,
        handleExpandedItems,
        hasPermissionForGroupTaskActions,
      ],
    );

    const getSampleAssessmentGroupChairmanActionsMenu = useCallback(
      (item: ITaskGroup, rowIndex: number): JSX.Element => {
        const menuItems: ActionMenuItem[] = [];
        const isChairmanExist =
          item.sampleAssessmentGroup && !!item.sampleAssessmentGroup.sampleAssessmentChairman.examiner;

        if (isChairmanExist) {
          menuItems.push({
            text: 'Usuń przewodniczącego z ZWO',
            onClick: () => {
              setAction('Usuń przewodniczącego z ZWO');
              displayRemoveSampleAssessmentGroupChairmanAssignmentPrompt();
            },
          });
          menuItems.push({
            text: 'Wyślij wiadomość',
            onClick: () => {
              setAction('Wyślij wiadomość');
              setMessageModal({ open: true, type: 'standard' });
            },
          });
        } else {
          menuItems.push({
            text: 'Przypisz KGZ do Przewodniczącego ZWO',
            onClick: () => {
              setAction('Przypisz KGZ do Przewodniczącego ZWO');
              setIsAssignSampleAssessmentGroupChairmanDialogVisible(true);
            },
          });
        }
        return <ActionsMenu menuItems={menuItems} rowIndex={rowIndex} />;
      },
      [displayRemoveSampleAssessmentGroupChairmanAssignmentPrompt, setAction, setMessageModal],
    );

    const getSampleAssessmentChairmanRow = useCallback(
      (sampleAssessmentChairmanRowData: {
        assessmentId: string;
        examinerNumber: string;
        firstName: string;
        id: string;
        lastName: string;
        rowBackground: string;
        rowIndex: number;
        sampleAssessmentGroup: SampleAssessmentGroup;
        taskGroup: ITaskGroup;
      }) => {
        const {
          assessmentId,
          examinerNumber,
          firstName,
          id,
          lastName,
          rowBackground,
          rowIndex,
          sampleAssessmentGroup,
          taskGroup,
        } = sampleAssessmentChairmanRowData;
        const itemId = sampleAssessmentGroup.sampleAssessmentChairman
          ? sampleAssessmentGroup.sampleAssessmentChairman.sampleAssessmentChairmanId
          : sampleAssessmentGroup.sampleAssessmentsGroupId;
        return {
          id,
          function: <div style={{ transform: 'translateX(40px)' }}>Przewodniczący ZWO</div>,
          firstName,
          lastName,
          examinerNumber,
          actions: (
            <>
              {hasPermissionForGroupTaskActions && (
                <div
                  onClick={() => {
                    if (taskGroup.sampleAssessmentGroup) {
                      handleClickMenu(
                        itemId,
                        taskGroup,
                        taskGroup.sampleAssessmentGroup.sampleAssessmentChairman.examiner,
                        undefined,
                        taskGroup.sampleAssessmentGroup.sampleAssessmentsGroupId,
                        undefined,
                        taskGroup.sampleAssessmentGroup.sampleAssessmentChairman,
                        assessmentId,
                      );
                    }
                  }}
                >
                  {getSampleAssessmentGroupChairmanActionsMenu(taskGroup, rowIndex)}
                </div>
              )}
            </>
          ),
          rowBackground,
        };
      },
      [getSampleAssessmentGroupChairmanActionsMenu, handleClickMenu, hasPermissionForGroupTaskActions],
    );

    const updateRows = useCallback(
      (limit: number) => {
        const newData: ExaminerTreemapTableItem[] = [];
        let rowIndex = -1;

        const isUnderLimit = () => {
          return rowIndex < limit;
        };

        props.data.forEach((item: ITaskGroup) => {
          const assessmentId = item.assessmentSession?.assessmentId ?? '';
          rowIndex++;
          const taskGroupId = item.taskGroupId;
          const isTaskGroupIdExpanded = isExpanded(taskGroupId, expandedItems);
          const taskGroupRowData = {
            id: item.taskGroupId,
            isExpanded: isTaskGroupIdExpanded,
            item,
            tooltipTitle: isTaskGroupIdExpanded ? 'Zwiń grupę zadań' : 'Rozwiń grupę zadań',
            assessmentId,
            rowIndex,
          };

          if (isUnderLimit()) {
            const taskGroupRow = getNewRow(taskGroupId, taskGroupRowData, getTaskGroupRow);
            newData.push(taskGroupRow);
          }

          if (isTaskGroupIdExpanded) {
            if (item.sampleAssessmentGroup) {
              rowIndex++;

              const sampleAssessmentGroupRowId = item.sampleAssessmentGroup.sampleAssessmentsGroupId;
              const isSampleAssessmentGroupRowExpanded = isExpanded(sampleAssessmentGroupRowId, expandedItems);
              const sampleAssessmentGroupRowData = {
                id: sampleAssessmentGroupRowId,
                tooltipTitle: isSampleAssessmentGroupRowExpanded ? 'Zwiń funkcję' : 'Rozwiń funkcję',
                isExpanded: isSampleAssessmentGroupRowExpanded,
                taskGroup: item,
                assessmentId,
                rowIndex,
                itemIsSelected: sampleAssessmentGroupRowId === selectedItemId.current,
              };
              if (isUnderLimit()) {
                const sampleAssessmentGroupRow = getNewRow(
                  sampleAssessmentGroupRowId,
                  sampleAssessmentGroupRowData,
                  getSampleAssessmentGroupRow,
                );
                newData.push(sampleAssessmentGroupRow);
              }

              if (isExpanded(item.sampleAssessmentGroup.sampleAssessmentsGroupId, expandedItems)) {
                rowIndex++;

                const { sampleAssessmentChairman } = item.sampleAssessmentGroup;
                const sampleAssessmentChairmanRowId = sampleAssessmentChairman.sampleAssessmentChairmanId;

                const rowBackground = determineRowBackgroundColor(
                  sampleAssessmentChairman.sampleAssessmentChairmanId,
                  sampleAssessmentChairman.examiner?.shouldBeHighlighted,
                  assessmentId,
                );

                const sampleAssessmentChairmanRowData = {
                  id: sampleAssessmentChairmanRowId,
                  firstName: sampleAssessmentChairman?.examiner ? sampleAssessmentChairman.examiner.firstName : '-',
                  lastName: sampleAssessmentChairman?.examiner ? sampleAssessmentChairman.examiner.lastName : '-',
                  examinerNumber: getExaminerNumberFromPermission(sampleAssessmentChairman.examiner, assessmentId),
                  sampleAssessmentGroup: item.sampleAssessmentGroup,
                  assessmentId,
                  taskGroup: item,
                  rowIndex,
                  rowBackground,
                };
                if (isUnderLimit()) {
                  const sampleAssessmentChairmanRow = getNewRow(
                    sampleAssessmentChairmanRowId,
                    sampleAssessmentChairmanRowData,
                    getSampleAssessmentChairmanRow,
                  );
                  newData.push(sampleAssessmentChairmanRow);
                }

                if (item.sampleAssessmentGroup.sampleAssessmentMembers) {
                  item.sampleAssessmentGroup.sampleAssessmentMembers.forEach((member) => {
                    rowIndex++;

                    const sampleAssessmentMemberId = member.sampleAssessmentMemberId;
                    const rowBackground = determineRowBackgroundColor(
                      member?.examiner.examinerId,
                      member?.examiner?.shouldBeHighlighted,
                      assessmentId,
                    );

                    const sampleAssessmentMemberRowData = {
                      assessmentId,
                      examinerNumber: getExaminerNumberFromPermission(member.examiner, assessmentId),
                      id: sampleAssessmentMemberId,
                      member,
                      rowBackground,
                      rowIndex,
                      taskGroup: item,
                    };

                    if (isUnderLimit()) {
                      const sampleAssessmentMemberRow = getNewRow(
                        sampleAssessmentMemberId,
                        sampleAssessmentMemberRowData,
                        getSampleAssessmentMemberRow,
                      );
                      newData.push(sampleAssessmentMemberRow);
                    }
                  });
                }
              }
            }
            item.examTaskGroupCoordinators.forEach((examTaskGroupCoordinator) => {
              if (!examTaskGroupCoordinator.hasToBeHidden) {
                rowIndex++;

                const examTaskGroupCoordinatorId = examTaskGroupCoordinator.taskGroupCoordinatorId;

                const isExamTaskGroupCoordinatorExpanded = isExpanded(examTaskGroupCoordinatorId, expandedItems);
                const rowBackground = determineRowBackgroundColor(
                  examTaskGroupCoordinator.taskGroupCoordinatorId,
                  examTaskGroupCoordinator.examiner?.shouldBeHighlighted,
                  assessmentId,
                );
                const examTaskGroupCoordinatorRowData = {
                  assessmentId,
                  examinerNumber: getExaminerNumberFromPermission(examTaskGroupCoordinator.examiner, assessmentId),
                  examTaskGroupCoordinator,
                  firstName: examTaskGroupCoordinator.examiner ? examTaskGroupCoordinator.examiner.firstName : '-',
                  id: examTaskGroupCoordinatorId,
                  isExpanded: isExamTaskGroupCoordinatorExpanded,
                  lastName: examTaskGroupCoordinator.examiner ? examTaskGroupCoordinator.examiner.lastName : '-',
                  rowBackground,
                  rowIndex,
                  taskGroup: item,
                  tooltipTitle: isExamTaskGroupCoordinatorExpanded ? 'Zwiń funkcję' : 'Rozwiń funkcję',
                };
                if (isUnderLimit()) {
                  const examTaskGroupCoordinatorRow = getNewRow(
                    examTaskGroupCoordinatorId,
                    examTaskGroupCoordinatorRowData,
                    getExamTaskGroupCoordinatorRow,
                  );
                  newData.push(examTaskGroupCoordinatorRow);
                }
              }

              if (
                isExpanded(examTaskGroupCoordinator.taskGroupCoordinatorId, expandedItems) ||
                (examTaskGroupCoordinator.hasToBeHidden && isExpanded(item.taskGroupId, expandedItems))
              ) {
                examTaskGroupCoordinator.examiningTeamChairmen.forEach((examiningTeamChairmen) => {
                  rowIndex++;

                  const examiningTeamChairmenid = examiningTeamChairmen.teamChairmanId;
                  const isExaminingTeamChairmenExpanded = isExpanded(
                    examiningTeamChairmen.teamChairmanId,
                    expandedItems,
                  );
                  const rowBackground = determineRowBackgroundColor(
                    examiningTeamChairmen.teamChairmanId,
                    examiningTeamChairmen.examiner?.shouldBeHighlighted,
                    assessmentId,
                  );
                  const examiningTeamChairmenRowData = {
                    assessmentId,
                    examinerNumber: getExaminerNumberFromPermission(examiningTeamChairmen.examiner, assessmentId),
                    examiningTeamChairmen,
                    firstName: examiningTeamChairmen.examiner ? examiningTeamChairmen.examiner.firstName : '-',
                    id: examiningTeamChairmenid,
                    isBlocked: examiningTeamChairmen.examiner?.examinerPermission[0]?.isBlocked,
                    isRowExpanded: isExaminingTeamChairmenExpanded,
                    lastName: examiningTeamChairmen.examiner ? examiningTeamChairmen.examiner.lastName : '-',
                    rowBackground,
                    rowIndex,
                    taskGroup: item,
                    tooltipTitle: isExaminingTeamChairmenExpanded ? 'Zwiń funkcję' : 'Rozwiń funkcję',
                  };
                  if (isUnderLimit()) {
                    const examiningTeamChairmenRow = getNewRow(
                      examiningTeamChairmenid,
                      examiningTeamChairmenRowData,
                      getExaminingTeamChairmenRow,
                    );
                    newData.push(examiningTeamChairmenRow);
                  }

                  if (isExpanded(examiningTeamChairmen.teamChairmanId, expandedItems)) {
                    examiningTeamChairmen.examiningTeam?.examiningTeamMembers.forEach((teamMember) => {
                      rowIndex++;
                      const examiningTeamMemberId = `${examiningTeamChairmen.teamChairmanId}-${teamMember.examiner.examinerId}`;
                      const rowBackground = determineRowBackgroundColor(
                        teamMember.examiner.examinerId,
                        teamMember.examiner?.shouldBeHighlighted,
                        assessmentId,
                      );
                      const examiningTeamMemberRowData = {
                        assessmentId,
                        examinerNumber: getExaminerNumberFromPermission(teamMember.examiner, assessmentId),
                        firstName: teamMember.examiner.firstName,
                        id: examiningTeamMemberId,
                        isBlocked: teamMember.examiner?.examinerPermission[0].isBlocked,
                        lastName: teamMember.examiner.lastName,
                        rowBackground,
                        rowIndex,
                        taskGroup: item,
                        teamMember,
                      };
                      if (isUnderLimit()) {
                        const examiningTeamMemberRow = getNewRow(
                          examiningTeamMemberId,
                          examiningTeamMemberRowData,
                          getExaminingTeamMemberRow,
                        );

                        newData.push(examiningTeamMemberRow);
                      }
                    });
                  }
                });
              }
            });
          }
        });

        allTreemapItemsCount.current = rowIndex + 1;
        if (renderStructureItemsCount > allTreemapItemsCount.current) {
          setRenderStructureItemsCount(Math.max(allTreemapItemsCount.current, treemapItemsPagination));
        }
        setTreemapData(newData);
      },
      [
        props.data,
        renderStructureItemsCount,
        expandedItems,
        getTaskGroupRow,
        getSampleAssessmentGroupRow,
        determineRowBackgroundColor,
        getSampleAssessmentChairmanRow,
        getSampleAssessmentMemberRow,
        getExamTaskGroupCoordinatorRow,
        getExaminingTeamChairmenRow,
        getExaminingTeamMemberRow,
      ],
    );

    useEffect(() => {
      updateRows(renderStructureItemsCount);
    }, [props.data, expandedItems, hasPermissionForGroupTaskActions, updateRows, renderStructureItemsCount]);

    const handleFilterChange = useCallback((name: string, value: string | string[]) => {
      const singleValue = Array.isArray(value) ? value[0] : value;
      if (name === 'taskGroupName') {
        props.handleTaskGroupFilterChange(singleValue);
      } else if (name === 'function') {
        props.handleFunctionsFilterChange(singleValue);
      } else {
        props.handleSearchInput(name, singleValue);
      }
    }, []);

    const filterElements: { [key: string]: JSX.Element } = useMemo(
      () => ({
        taskGroupName: (
          <>
            <TooltipOne title="Ustaw filtr wyszukiwania: Grupa zadań" placement="top">
              <FilterListIcon
                className={classes.filterListIcon}
                style={{ paddingLeft: '15px' }}
                onClick={handleClickTaskGroupMenu}
                aria-controls="TaskGroup-menu"
                aria-haspopup="true"
                fill={fill}
              />
            </TooltipOne>
            <DropdownMenuFilter
              anchorEl={anchorElTaskGroup}
              setAnchorEl={setAnchorElTaskGroup}
              menuItems={props.filterTaskGroupsMenuItems}
              hasCheckbox={true}
              checkedItems={props.checkedTaskGroupsMenuItems}
              onChange={props.handleTaskGroupFilterChange}
              uncheckAllMenuItems={props.uncheckAllTaskGroups}
            />
          </>
        ),
        function: (
          <>
            <TooltipOne title="Ustaw filtr wyszukiwania: Funkcja" placement="top">
              <FilterListIcon
                className={classes.filterListIcon}
                onClick={handleClickFunctionMenu}
                aria-controls="TaskGroup-menu"
                aria-haspopup="true"
              />
            </TooltipOne>
            <DropdownMenuFilter
              anchorEl={anchorElFunction}
              setAnchorEl={setAnchorElFunction}
              menuItems={props.filterFunctionsMenuItems}
              hasCheckbox={true}
              checkedItems={props.checkedFunctionsMenuItems}
              onChange={props.handleFunctionsFilterChange}
              uncheckAllMenuItems={props.uncheckAllFunctions}
            />
          </>
        ),
      }),
      [
        anchorElFunction,
        anchorElTaskGroup,
        classes.filterListIcon,
        handleClickFunctionMenu,
        handleClickTaskGroupMenu,
        props.checkedFunctionsMenuItems,
        props.checkedTaskGroupsMenuItems,
        props.filterFunctionsMenuItems,
        props.filterTaskGroupsMenuItems,
        props.handleFunctionsFilterChange,
        props.handleTaskGroupFilterChange,
        props.uncheckAllFunctions,
        props.uncheckAllTaskGroups,
      ],
    );

    const filterbar = useMemo(
      () => (props.isAnyFiltersActive ? props.filterState : []),
      [props.filterState, props.isAnyFiltersActive],
    );

    if (!isSessionLoaded || !props.assessmentSessionId || treemapData === undefined) {
      return null;
    }

    return (
      <>
        <div
          className={[tableClasses.adminTableContainerFlex, tableClasses.adminTableContainerFullWidth].join(' ')}
          style={{ width: '100%', padding: 0, paddingTop: '20px', maxHeight: 'calc(100vh - 260px)' }}
        >
          <SelectableTable<ExaminerTreemapTableItem>
            columns={treemapColumns}
            rows={treemapData}
            onFilterChange={handleFilterChange}
            filter={defaultFilter}
            filterElements={filterElements}
            closeFilterItem={closeFilterItem}
            uncheckAllFilters={props.uncheckAllFilters}
            filterbar={filterbar}
            caption="Zarządzanie strukturą egzaminatorów"
            footer={
              allTreemapItemsCount.current > renderStructureItemsCount ? (
                <CircularProgress size={20} style={{ marginTop: '20px' }} />
              ) : undefined
            }
            onScrollTable={(endReached) => {
              if (endReached) {
                setRenderStructureItemsCount((prevValue) => {
                  const newValue =
                    prevValue < allTreemapItemsCount.current
                      ? prevValue + treemapItemsPagination
                      : Math.max(allTreemapItemsCount.current, treemapItemsPagination);
                  return newValue;
                });
              }
            }}
          />
        </div>

        {IsEmptyResult && props.data.length === 0 && <NoRecords />}
        {isAddSampleAssessmentGroupConfirmationVisible && selectedTaskGroup.current && (
          <AddSampleAssessmentGroupDialog
            isAddSampleAssessmentGroupConfirmationVisible={isAddSampleAssessmentGroupConfirmationVisible}
            hideAddSampleAssessmentGroupConfirmation={() => setIsAddSampleAssessmentGroupConfirmationVisible(false)}
            taskGroup={selectedTaskGroup.current}
            onSuccess={() => {
              handleReload();
              setIsAddSampleAssessmentGroupConfirmationVisible(false);
            }}
            onReload={reload}
            onFailure={(isNewSampleGroupCreated: boolean) => {
              if (isNewSampleGroupCreated) {
                handleReload();
              }
              setIsAddSampleAssessmentGroupConfirmationVisible(false);
            }}
          />
        )}
        {selectedSampleAsssessmentGroup &&
          selectedTaskGroup.current &&
          isAssignSampleAssessmentGroupChairmantDialogVisible && (
            <SampleAssessmentGroupChairmanAssignDialog
              taskGroup={selectedTaskGroup.current}
              sampleAssessmentId={selectedSampleAsssessmentGroup}
              onSuccess={() => {
                handleReload();
                setIsAssignSampleAssessmentGroupChairmanDialogVisible(false);
              }}
              onFailure={() => {
                setIsAssignSampleAssessmentGroupChairmanDialogVisible(false);
              }}
            />
          )}
        {selectedExaminer.current && openDisableExaminerPrompt && (
          <DisableExaminer
            onSuccess={() => {
              handleReload();
              setOpenDisableExaminerPrompt(false);
            }}
            onFailure={() => {
              setOpenDisableExaminerPrompt(false);
            }}
            examiner={selectedExaminer.current}
            isExaminingTeamChairmen={isExaminingTeamChairmen}
          />
        )}
        {selectedSecondaryId.current && openDisableExaminingTeamPrompt && (
          <DisableExaminingTeam
            onSuccess={() => {
              handleReload();
              setOpenDisableExaminingTeamPrompt(false);
            }}
            onFailure={() => {
              setOpenDisableExaminingTeamPrompt(false);
            }}
            examiningTeamId={selectedSecondaryId.current}
          />
        )}
        {selectedExaminer.current && openEnableExaminerPrompt && (
          <EnableExaminer
            onSuccess={() => {
              handleReload();
              setOpenEnableExaminerPrompt(false);
            }}
            onFailure={() => {
              setOpenEnableExaminerPrompt(false);
            }}
            examiner={selectedExaminer.current}
            isExaminingTeamChairmen={isExaminingTeamChairmen}
            lockType={selectedExaminer.current.examinerPermission[0]?.isBlocked ?? BlockadeTypeId.Manual}
            isControlProcessActive={props.isControlProcessActive}
            examinerPermissionId={''}
            assessmentSessionId={props.assessmentSessionId}
          />
        )}
        {selectedTaskGroup.current && openAddKGZPrompt && (
          <AddKGZPrompt
            taskGroup={selectedTaskGroup.current}
            onSuccess={() => {
              handleReload();
              setOpenKGZPrompt(false);
            }}
            onFailure={() => {
              setOpenKGZPrompt(false);
            }}
          />
        )}

        {selectedExaminer.current && selectedTaskGroup.current && openUnassignDialog && (
          <UnassignExamTaskGroupCoordinatorDialog
            itemId={selectedItemId.current}
            examiner={selectedExaminer.current}
            taskGroup={selectedTaskGroup.current}
            onSuccess={() => {
              handleReload();
              setOpenUnassignDialog(false);
            }}
            onFailure={() => {
              setOpenUnassignDialog(false);
            }}
          />
        )}
        {openLimitCardPerExaminerDialog &&
          selectedExaminer.current &&
          selectedTaskGroup.current &&
          selectedSecondaryId.current && (
            <SessionsParametersCardLimitPerExaminerDialog
              selectedExaminer={selectedExaminer.current}
              selectedTaskGroup={selectedTaskGroup.current}
              examiningTeamId={selectedSecondaryId.current}
              onClose={() => setOpenLimitCardPerExaminerDialog(false)}
              onOpen={openLimitCardPerExaminerDialog}
            />
          )}
        <Prompt
          type="confirmation"
          isBold={true}
          open={isConfirmationPromptVisible}
          mainText={promptMainText}
          subTexts={promptSubTexts}
          onConfirm={() => {
            setIsConfirmationPromptVisible(false);
            switch (action) {
              case 'Usuń funkcję PZE':
                removeExaminingTeamChairmanFunction();
                break;
              case 'Usuń przewodniczącego z ZWO':
                removeSampleAssessmentChairmanAssignment();
                break;
              case 'Usuń członka ZWO':
                removeSampleAssessmentMemberAssignment();
                break;
              case 'Usuń egzaminatorowi funkcję PZE':
                removeExaminignTeamChairmanAssignment();
                break;
              case 'Usuń ZWO':
                removeSampleAssessmentGroup();
                break;
            }
          }}
          onCancel={() => setIsConfirmationPromptVisible(false)}
          mainActionButtonText="TAK"
        />
        <Prompt
          type="success"
          isBold={true}
          open={isSuccessPromptVisible}
          mainText={promptMainText}
          subTexts={promptSubTexts}
          onConfirm={() => {
            handleReload();
            setIsSuccessPromptVisible(false);
          }}
          mainActionButtonText="POWRÓT DO LISTY"
        />
        <Prompt
          type="error"
          isBold={true}
          open={isErrorPromptVisible}
          mainText={promptMainText}
          subTexts={promptSubTexts}
          onConfirm={() => setIsErrorPromptVisible(false)}
          mainActionButtonText="POWRÓT DO LISTY"
        />
        <NewMessageModal
          open={messageModal.open}
          type={messageModal.type}
          onClose={() => {
            selectedExaminer.current = undefined;
            setMessageModal({ open: false, type: 'standard' });
          }}
          onSend={() => setMessageModal({ open: false, type: 'standard' })}
          recipientWithFunctionIdFromExaminerTreemap={examiningTeamMember.current}
          session={sessionDataForNewMessage}
        ></NewMessageModal>
      </>
    );
  },
);
export default ExaminerTreemapTable;
ExaminerTreemapTable.displayName = 'ExaminerTreemapTable';
